// cspell:ignore unreviewed
import {
  all,
  call,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'typed-redux-saga/macro';
import { modules } from '@veritone/glc-redux';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { selectFeatureFlags } from '@common/user-permissions';
import {
  REDACTING_FILE,
  redactFileCompleted,
  selectRedactFiles,
} from '@common-modules/redactFile';
import { REDACTED_FILE_COMPLETED } from '@common-modules/mediaDetails/actions';
import { TDOId } from '@common-modules/universal/models/Brands';

import {
  DELETE_REDACTED_DOCUMENT_FAILURE,
  DELETE_REDACTED_DOCUMENT_REQUEST,
  DELETE_REDACTED_DOCUMENT_SUCCESS,
  DOWNLOAD_REDACTION_REPORT_FAILURE,
  DOWNLOAD_REDACTION_REPORT_REQUEST,
  LOAD_REDACTED_VERSION_PAGES,
  LOAD_REDACTED_VERSION_PAGES_FAILURE,
  LOAD_REDACTED_VERSION_PAGES_SUCCESS,
  DOCUMENT_BURN_PROGRESS,
  DOCUMENT_BURN_PROGRESS_DONE,
  EXPORT_COMPLETED,
  EXPORT_FAILED,
  EXPORT_REQUEST,
  EXPORT_SUBMITTED,
  INCREMENT_REDACTED_DOCUMENT_DOWNLOAD,
  LOG_AUDIT_EVENT,
  SAVE_FAILURE,
  SAVE_SUCCESS,
} from '../actions';
import {
  RedactedDocumentDetails,
  serviceDeleteAsset,
  serviceFetchTdoLatestRedactedDocumentVersion,
  serviceDownloadPiiResult,
  serviceFindPiiResultAssetUrl,
  serviceListAuditLogAssets,
  serviceListRedactedDocumentAssets,
  serviceLoadDocumentRedactionDataForRedactedFile,
  serviceSaveRedactedDocumentAsset,
  serviceUpdateAssetDetails,
  serviceUpdateTdoLatestRedactedDocumentVersion,
  serviceFetchDocumentMediaAssets,
  uploadDocumentAuditLog,
} from '../services';
import type { RedactionAssetWriter } from '../redactionAssetWriter';

/**
 * What the forced pre-burn save is stamped with for a file released without
 * review (VE-32440). Named once, next to the only thing that sets it.
 */
const UNREVIEWED_INCLUDE_WRITER: RedactionAssetWriter = 'unreviewed-include';
import {
  selectExportInterrupted,
  selectRedactAllRun,
  selectSession,
} from '../selectors';
import type { FrozenGovernance } from './redactAll';
import { selectConfigEngines } from '@common-modules/engines/selectors';
import { buffers, eventChannel } from 'redux-saga';
import type { BurnStage } from '../models';
import {
  burnInPdf,
  type BurnInProgressReport,
  type BurnInResult,
} from '../burnInWorkerClient';
import { drawableBatesSettings } from '../batesLabel';
import type { BurnInWatermark, BurnInWatermarkBates } from '../burnInWorker';
import { buildRedactedFilename } from '../filename';
import {
  buildPiiMatchBoxes,
  buildRedactionPlan,
  computeRedactionMetrics,
  excludedPagesFromAssetBody,
  GLYPH_REDACTION_REMOVAL_VINSET,
  glyphAnchoredVisualPadAmong,
  highestPageOf,
  isGlyphAnchoredRedaction,
  readAssetVersion,
  polygonToStrips,
} from '../helpers';
import { fetchPdfPageDimensions, readPdfPageCount } from '../pdfPageDimensions';
import { sagaIntl } from '@i18n';
import { findPdfOriginalMediaAsset } from '@redact-modules/casemedia/mediaAssets';
import { fetchFileOutputs } from '@redact-modules/casemedia/services';
import { selectBatesGovernanceFor } from '@redact-modules/projects/renderedBates';
import { batesSpanOverrun } from '@redact-modules/projects/batesStamp';
import {
  selectWatermarkGovernanceFor,
  selectWatermarkProjectFor,
} from '@redact-modules/projects/renderedWatermarks';
import { LOAD_PROJECT_REDACTION_REQUEST } from '@redact-modules/projects/actions';
import { handleLoadProjectRedaction } from '@redact-modules/projects/projectRedactionSaga';
import { watermarksToDraw } from '@redact-modules/projects/projectRedactionAsset';
import {
  formatDocumentRedactionReportCsv,
  PiiBoxMap,
} from '../redactionReportCsv';
import {
  AppliedWatermark,
  BatesStampSettings,
  clampWatermarkOpacity,
  drawnTextColor,
  DocumentRedactionAssetV1,
  SessionState,
} from '../models';
import { fontScaleOf, legacyTextScale } from '../watermarkTextMetrics';
import { parseUndrawableWatermarkError } from '../glyphOutlines';
import { GraphQLConfig, selectToken, selectGraphQLConfig } from './auth';
import { persistSessionSnapshot } from './autoSave';

// Strip height (PDF user-space units) for decomposing polygon redactions into
// axis-aligned rects at burn-in. Smaller = smoother (finer staircase) but more
// redaction annotations. 0.75pt keeps slanted edges visually smooth at normal
// zoom while staying well within MuPDF's per-page annotation budget.
const POLYGON_STRIP_HEIGHT = 0.75;

// VE-25718: how many times the pre-export save may be re-issued when an edit
// lands while it is in flight. Each retry costs one save round-trip, and a user
// editing continuously would otherwise hold the export open forever — 3 covers
// the realistic case (a stray drag during one save) without stalling.
const MAX_PRE_EXPORT_SAVE_ATTEMPTS = 3;

/**
 * Decode a base64 `data:` URL (the uploaded watermark image, VE-24693) to raw
 * bytes for the burn-in worker. Runs on the main thread where `atob` exists.
 */
function dataUrlToArrayBuffer(dataUrl: string): ArrayBuffer {
  // Guarded rather than trusted: `atob` throws on anything that is not base64,
  // and this runs inside the export saga, so one poisoned watermark from shared
  // storage would fail the whole burn-in rather than just not drawing. The
  // boundary validators should already have dropped it; this is the backstop.
  const comma = dataUrl.indexOf(',');
  if (!dataUrl.startsWith('data:') || comma < 0) {
    throw new Error('Watermark image is not an inline data URL');
  }
  const base64 = dataUrl.slice(comma + 1);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Convert the document's Bates settings (VE-24447) into the burn-in worker's
 * payload, with every bounded field clamped. Always returns a mark — whether
 * there is a stamp at all is the CALLER's question, answered from the merged
 * document's governance, `saved.batesStamp`, the feature flag and the span
 * check.
 *
 * A near-pass-through, deliberately: the shapes are kept aligned so this stays
 * a mapping rather than a translation with rules of its own. The one thing it
 * does own is refusing degenerate input — a non-positive font size draws
 * nothing, and a non-finite start number would stamp "NaN" on every page.
 */
export function toBurnInBatesStamp(
  bates: BatesStampSettings
): BurnInWatermarkBates {
  // CLAMPS, and never returns null. Returning null here was a real defect: it
  // dropped the whole mark from the payload for `fontSize <= 0`, which an
  // operator reaches just by clearing the font-size field to retype it
  // (`Number('') === 0`). The export then succeeded with no Bates numbers on any
  // page while the rail badge, the modal preview and the canvas overlay all
  // reported it applied — and reopening the document clamped the same stored 0
  // up to the minimum, so one asset produced two different released records.
  //
  // An out-of-range value is a range violation, not a type violation, and the
  // contract for those is to clamp. Losing the identifier is never the safer
  // failure for a record a production is cited by.
  // `drawable`, not `clamped`: this is bounded AND reduced to what the burn-in
  // font can actually draw, which the asset reader deliberately does not do.
  const drawable = drawableBatesSettings(bates);
  return { ...drawable, kind: 'bates' };
}

/**
 * Convert an applied watermark (VE-24693) into the burn-in worker's payload,
 * decoding the image data URL to bytes. Returns null for a watermark that has
 * nothing to draw (empty text, or an image with no data URL) so the caller can
 * drop it. Opacity is clamped defensively (also enforced in the reducer).
 */
export function toBurnInWatermark(
  wm: AppliedWatermark
): BurnInWatermark | null {
  if (wm.type === 'image') {
    if (!wm.imageDataUrl) {
      return null;
    }
    return {
      kind: 'image',
      bytes: dataUrlToArrayBuffer(wm.imageDataUrl),
      opacity: clampWatermarkOpacity(wm.opacity),
      scale: wm.scale,
      rotation: wm.rotation,
      placement: wm.placement,
      // Carried, not recomputed: an anchored mark is positioned by the SAME
      // helper on both sides, so what the worker needs is the anchor the
      // operator chose rather than a point this side resolved.
      anchorH: wm.anchorH,
      anchorV: wm.anchorV,
      inset: wm.inset,
      customXPct: wm.customXPct,
      customYPct: wm.customYPct,
      x: wm.x,
      y: wm.y,
    };
  }
  if (!wm.text.trim()) {
    return null;
  }
  return {
    kind: 'text',
    text: wm.text,
    // Resolved here, not in the worker: the overlay draws through the same
    // helper, so preview and export can't disagree about grayscale.
    color: drawnTextColor(wm),
    opacity: clampWatermarkOpacity(wm.opacity),
    // Size travels as a coefficient of page width applied to the FONT SIZE;
    // the worker resolves it against each page's own width.
    //
    // Through `fontScaleOf`, not `wm.fontScale` directly, so a mark restored
    // from an asset written before the split is converted here exactly as the
    // editor converts it — the preview and the export must not read the same
    // record through two different resolvers.
    fontScale: fontScaleOf(wm),
    // The legacy mirror, computed rather than copied. `wm.scale` is a frozen
    // default on a text mark and means nothing; this is what the OLD worker's
    // formula needs to arrive at the same size, so a rolled-back client renders
    // correctly instead of at half the page width.
    scale: legacyTextScale(wm),
    anchorH: wm.anchorH,
    anchorV: wm.anchorV,
    inset: wm.inset,
    bold: wm.bold,
    align: wm.align,
    customXPct: wm.customXPct,
    customYPct: wm.customYPct,
    placement: wm.placement,
    x: wm.x,
    y: wm.y,
    rotation: wm.rotation,
  };
}

/**
 * Compute the next version number for the redacted-document asset.
 *
 * Mirrors the imageRedactionSaga pattern: prefer the MAX of `details.version`
 * across existing redacted-document assets, fall back to the TDO-level
 * `details.latestRedactedDocumentVersion` (so version doesn't reset if every
 * prior asset has been rotated away), and finally default to 0 → first
 * version is 1.
 */
function* computeNextVersion(tdoId: string, token: string, cfg: GraphQLConfig) {
  const existing = yield* call(
    serviceListRedactedDocumentAssets,
    tdoId,
    token,
    cfg.apiRoot,
    cfg.graphQLEndpoint,
    cfg.veritoneAppId
  );
  // 1) Start from the max version found on any surviving redacted-document
  //    asset. Per ticket policy we delete all-but-newest, so this is usually
  //    just one asset.
  let version = 1;
  const versions = existing
    .map((a) => readAssetVersion(a.details))
    .filter((v) => v > 0);
  if (versions.length > 0) {
    version = Math.max(...versions) + 1;
  }
  // 2) Also check the TDO-level cache. This handles two cases the asset list
  //    can't: (a) every prior asset was manually deleted, and (b) the asset
  //    list returned `details` in a shape we couldn't parse. Take whichever
  //    is higher. Mirrors imageRedactionSaga's pattern.
  const persistedVersion = yield* call(
    serviceFetchTdoLatestRedactedDocumentVersion,
    tdoId,
    token,
    cfg.apiRoot,
    cfg.graphQLEndpoint,
    cfg.veritoneAppId
  );
  if (typeof persistedVersion === 'number' && persistedVersion >= version) {
    version = persistedVersion + 1;
  }
  return { nextVersion: version, existing };
}

/**
 * Best-effort cleanup of all PRIOR redacted-document assets after a fresh
 * burn-in. Per the ticket: "Remove all older redacted-document assets (later
 * we can consider keeping older versions)." Failures here never fail the
 * user-visible export.
 */
function* removeOlderRedactedDocumentAssets(
  prior: Array<{ id: string }>,
  newAssetId: string,
  token: string,
  cfg: GraphQLConfig
) {
  const toDelete = prior.filter((a) => a.id !== newAssetId);
  for (const a of toDelete) {
    // Best-effort cleanup — a single stray asset failing to delete
    // shouldn't tank the burn-in itself. `serviceDeleteAsset` throws on
    // failure now; we log + continue so subsequent deletes still run.
    try {
      yield* call(
        serviceDeleteAsset,
        a.id,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      );
    } catch (e) {
      console.warn(`Older redacted-document delete failed for ${a.id}:`, e);
    }
  }
}

/** Best-effort delete of a batch of audit-log asset ids. Non-fatal. */
function* deleteAuditLogAssets(
  ids: string[],
  token: string,
  cfg: GraphQLConfig
) {
  for (const id of ids) {
    try {
      yield* call(
        serviceDeleteAsset,
        id,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      );
    } catch (e) {
      console.warn(`Audit-log asset delete failed for ${id}:`, e);
    }
  }
}

/**
 * Rotate `redact-audit-log` assets in lockstep with the redacted-document
 * assets: keep only the just-created one, delete all prior ones. Without this
 * the audit-log CSVs accumulate (one per burn-in) even though we keep only the
 * latest redacted-document asset. Best-effort — never fails the export.
 */
function* removeOlderAuditLogAssets(
  tdoId: string,
  keepAssetId: string,
  token: string,
  cfg: GraphQLConfig
) {
  try {
    const all = yield* call(
      serviceListAuditLogAssets,
      tdoId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    const stale = all.filter((a) => a.id !== keepAssetId).map((a) => a.id);
    yield* call(deleteAuditLogAssets, stale, token, cfg);
  } catch (e) {
    console.warn('Audit-log rotation failed (non-fatal):', e);
  }
}

/**
 * What the pre-export save settled on.
 *
 * `ok` is false only when a save actually FAILED — a still-dirty session after
 * the last attempt is not an error, just a race the user is still winning.
 *
 * `snapshot` is the session state the last save persisted. It is what the
 * burn-in renders when the editor has been closed in the meantime (VE-28061):
 * the store then holds no session (or another document's), and the export must
 * not fall back to it.
 */
export interface PreExportSave {
  ok: boolean;
  snapshot: SessionState;
}

/**
 * How long the export waits for an already-running save to settle before
 * persisting its own (VE-28061). Generous — the point is only to avoid
 * overlapping two saves, and one that takes longer than this has almost
 * certainly failed in a way SAVE_FAILURE reports anyway. Bounded so a lost
 * reply can never hold a burn-in open forever.
 */
const IN_FLIGHT_SAVE_WAIT_MS = 30_000;

/**
 * Wait for a save already in flight on this document to settle.
 *
 * VE-28061: the export used to reach the persist step through `SAVE_NOW`, which
 * the single-threaded autosave loop executed — so its save could never overlap
 * another. Persisting directly removed that serialization, and two concurrent
 * saves are not merely wasteful: each writes a full `document-redaction-data`
 * asset, only the export's carries the VE-26422 burn-in label, and
 * `pickNewestRedactionData` picks by `createdDateTime`. If the unlabelled one
 * commits last the file reads "missing latest edits" permanently even though
 * its output is exactly current, and a late `SAVE_SUCCESS` can reset
 * `lastSavedBurnInVersion` to null and raise the stale banner immediately after
 * a successful burn-in. The two rotations would also race on the same deletes.
 *
 * Every other save entry point is already mutually exclusive by construction:
 * `hasUnsavedWork` excludes `'saving'`, so the autosave interval skips and both
 * Save buttons disable while one is in flight. This restores that property for
 * the export, which is now the only caller that persists directly.
 */
function* awaitInFlightSave(tdoId: TDOId) {
  const session = yield* select(selectSession);
  if (session?.tdoId !== tdoId || session.save.status !== 'saving') {
    return;
  }
  const settlesThisDocument = (a: {
    type: string;
    payload?: { tdoId?: TDOId };
  }) =>
    (a.type === SAVE_SUCCESS.type || a.type === SAVE_FAILURE.type) &&
    a.payload?.tdoId === tdoId;
  const { timeout } = yield* race({
    settled: take(settlesThisDocument),
    timeout: delay(IN_FLIGHT_SAVE_WAIT_MS),
  });
  if (timeout) {
    console.warn(
      '[redact] pre-export save: timed out waiting for an in-flight save to settle; proceeding.'
    );
  }
}

/**
 * Force a save before burning in, re-saving while the session is still dirty.
 *
 * Saves the LIVE session while it is still this document's, so edits made
 * between the click and the save are included (VE-25718). Once the editor has
 * been closed — or shows another document — the store has nothing newer than
 * the snapshot already held, so that is what gets saved. Skipping the save in
 * that case is not an option: the save is what carries the VE-26422 burn-in
 * label, and without it the newest redaction asset reads as "never burned in"
 * and the file is reported stale for good (the trap VE-27738 fixed for images).
 *
 * Persists directly through `persistSessionSnapshot` rather than via SAVE_NOW.
 * The autosave loop only observes SAVE_NOW while parked between saves and
 * ignores it entirely once the session is closed, so the indirection left the
 * export waiting forever in both cases (VE-28061).
 *
 * Exported for its own tests: worth pinning without standing up the whole
 * burn-in, which is why it lives outside the EXPORT_REQUEST handler.
 */
export function* saveBeforeExport(
  nextVersion: number,
  initialSession: SessionState,
  /**
   * Stamp the forced save as machine-written (VE-32440).
   *
   * Set only for a file the reviewer chose to release WITHOUT reviewing it.
   * This save persists an empty redaction plan that nobody drew, and left
   * unmarked it reads as a reviewer's save for the rest of the file's life —
   * which deleted the very state the consent dialog keys on, so the next
   * pass burned the file again with nothing asked.
   */
  writtenBy?: RedactionAssetWriter
): Generator<any, PreExportSave, any> {
  let snapshot = initialSession;
  for (let attempt = 1; attempt <= MAX_PRE_EXPORT_SAVE_ATTEMPTS; attempt += 1) {
    yield* awaitInFlightSave(snapshot.tdoId);
    const live = yield* select(selectSession);
    if (live && live.tdoId === snapshot.tdoId) {
      snapshot = live;
    }
    const saveRequestId = `export-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const result = yield* persistSessionSnapshot(snapshot, {
      requestId: saveRequestId,
      expectedRedactedVersion: nextVersion,
      writtenBy,
    });
    if (!result.ok) {
      return { ok: false, snapshot };
    }
    const settled = yield* select(selectSession);
    // Only an OPEN editor showing this document can have got dirtier during
    // the save; a closed one has nothing left to re-save.
    if (settled?.tdoId !== snapshot.tdoId || settled.save.status !== 'dirty') {
      break;
    }
  }
  return { ok: true, snapshot };
}

const {
  user: { LOGOUT },
} = modules;

/**
 * The session the burn-in renders from: the live session when it is still this
 * document's (it may carry edits made during the save), otherwise the snapshot
 * the pre-export save persisted. Resolved ONCE; nothing after it reads
 * `selectSession` again, which is what lets the export run to completion after
 * the editor is closed (VE-28061).
 */
export const resolveExportSource = (
  live: SessionState | null,
  snapshot: SessionState
): SessionState => (live && live.tdoId === snapshot.tdoId ? live : snapshot);

/**
 * Every failure exit of the burn-in: record it on the session (a no-op if this
 * document is no longer open) and tell the user. The console carries the real
 * cause; the toast stays generic. The cross-app "Redacting" indicator is NOT
 * released here — `handleExportRequest` owns it and releases it once, in its
 * `finally`, on every exit (VE-28061).
 */
function* failExport(tdoId: TDOId, message: string, userMessage: string) {
  yield* put(EXPORT_FAILED({ tdoId, message }));
  yield* put(enqueueSnackbar({ message: userMessage }));
}

/**
 * The burn-in itself: save, fetch, render, upload, rotate, announce.
 *
 * Split out of the EXPORT_REQUEST watcher so the watcher can own the
 * "Redacting" indicator in a try/finally and bound the run with LOGOUT
 * (VE-28061), and so this can be driven directly in tests rather than reached
 * through the watcher.
 *
 * `initialSession` predates every round-trip below, so it is NOT what the
 * burn-in renders from; use it only for things settled before the save, like
 * the TDO id. See the re-read after the save (VE-25718).
 */
/**
 * One burn's progress, as a channel the saga can `put` from (VE-31309).
 *
 * ONLY THE PROGRESS GOES THROUGH HERE. The burn itself stays a plain
 * `yield* call(burnInPdf, …)`, which matters more than the symmetry: these
 * sagas are tested by driving their effects, so a `burnInPdf` moved off the
 * effect stream stops being mockable and a unit test spawns a real worker
 * under jsdom. `lexiconApply` wraps its whole async call for the opposite
 * reason — there is nothing else intercepting it there.
 *
 * An expanding buffer because reports arrive while the pump is busy
 * dispatching the previous one. Closing the channel ends the pump: a taker on
 * a closed channel is terminated, which is exactly the exit wanted.
 */
const makeBurnProgressChannel = (
  register: (emit: (report: BurnInProgressReport) => void) => void
) =>
  eventChannel<BurnInProgressReport>((emit) => {
    register(emit);
    return () => undefined;
  }, buffers.expanding<BurnInProgressReport>());

/**
 * Say which stage of the pipeline a file is in (VE-31309).
 *
 * THE STAGES THE WORKER CANNOT SEE. Persisting the reviewer's redactions,
 * pulling the source PDF down and pushing the redacted one back up all happen
 * out here, and two of them are network — on a large scan over a slow link
 * they are most of the wait. Reporting only the WASM stages left the longest
 * silences unexplained.
 *
 * No position, because none of these has one: the fetch reports bytes nobody
 * can turn into pages, and the upload is a single request.
 */
function* reportStage(tdoId: string, stage: BurnStage) {
  yield* put(
    DOCUMENT_BURN_PROGRESS({
      tdoId,
      stage,
      done: 0,
      total: 0,
      page: null,
      pageCount: 0,
    })
  );
}

/**
 * Turns reports into actions until the channel closes.
 *
 * EXPORTED for its own test. It cannot be reached through the export saga's:
 * that one drives effects by hand, and a forked loop parked on `take` never
 * returns to the driver.
 */
export function* pumpBurnProgress(
  tdoId: string,
  chan: ReturnType<typeof makeBurnProgressChannel>
) {
  while (true) {
    const report = yield* take(chan);
    yield* put(
      DOCUMENT_BURN_PROGRESS({
        tdoId,
        stage: report.stage,
        done: report.done,
        total: report.total,
        page: report.page,
        pageCount: report.pageCount,
      })
    );
  }
}

export function* runDocumentBurnIn(
  tdoId: TDOId,
  initialSession: SessionState,
  /**
   * What governs this file, resolved by the CALLER at the press.
   *
   * A single click resolves it here and that is correct — here IS the click.
   * A pass cannot: it runs for minutes, and all of this resolves through the
   * open request, which returning to the folder view clears. Resolved per
   * file mid-pass it comes back `ungoverned`, which is a legitimate answer
   * for a document in no merged set and therefore warns about nothing, while
   * the member burns its own leftover per-file stamp and none of the set's
   * watermarks. See `freezeGovernanceAtPress`.
   */
  frozen?: FrozenGovernance,
  /**
   * This file is being released WITHOUT having been reviewed — the reviewer
   * ticked it in `IncludeUnworkedDialog` (VE-32440).
   *
   * Three things follow from it, and all three are about leaving a trace:
   * the forced pre-burn save is marked machine-written so the choice does
   * not erase its own precondition, the audit event says what happened, and
   * the output asset records it. The BURN ITSELF is unchanged — an empty
   * plan is an empty plan — which is why this is a reporting flag and not a
   * branch in the render.
   */
  unreviewed?: boolean
) {
  const token: string = yield* select(selectToken);
  const cfg = yield* select(selectGraphQLConfig);
  const { documentOcrEngineId } = yield* select(selectConfigEngines);
  // The principal who clicked Redact File, frozen NOW. This run outlives the
  // editor and can outlive the login (see the LOGOUT race in the watcher), so
  // anything that resolves credentials from the store must read the store as
  // it was at the click — never at completion, when a different user may be
  // signed in on the same tab. `token` / `cfg` above cover the direct service
  // calls; this snapshot feeds the audit-log upload, whose helpers take a
  // getState accessor (VE-28061).
  const stateAtClick = yield* select((s: unknown) => s);

  /**
   * WHAT GOVERNS THIS FILE'S NUMBERING, frozen at the click for the same
   * reason the principal is.
   *
   * A member of a merged document takes the SET's stamp (VE-30105) at a start
   * derived from its position; nothing is stored on the member, so it is
   * resolved from the project. That resolution reads the OPEN REQUEST, and
   * returning to the folder view clears it — while this run carries on, since
   * a document burn-in outlives the editor (VE-28061). Read at the end it
   * would come back `ungoverned` and the member would burn its own leftover
   * per-file stamp, landing on numbers another member had already drawn.
   *
   * Resolved for `tdoId`, not for whatever document is on screen.
   */
  const batesGovernance =
    frozen?.bates ?? (yield* select(selectBatesGovernanceFor(tdoId)));
  /**
   * AND WHAT WATERMARKS IT CARRIES (VE-30104), frozen here for the same
   * reason: a member of a merged document draws the SET's marks, resolved
   * from the project, and that resolution reads the open request — which
   * returning to the folder view clears while this run carries on.
   */
  let watermarkGovernance =
    frozen?.watermark ?? (yield* select(selectWatermarkGovernanceFor(tdoId)));
  /**
   * AND WHETHER IT IS A MEMBER AT ALL, from the same freeze as the two above.
   *
   * `selectWatermarkProjectFor` answers with the governing project ONLY for a
   * member, so its presence is the membership test — but its own contract
   * says it is SCOPED TO THE OPEN REQUEST and must be read AT THE CLICK,
   * because leaving for the folder view clears the request and a later read
   * answers `undefined`. It was being read at COMPLETION, where `undefined`
   * means "we did not look" and decides the tab flip wrongly for a member —
   * exactly the failure the payload's own comment warns about.
   *
   * `freezeGovernanceAtPress` already captured it as
   * `watermarkProjectTdoId` and nothing read it; a pass starts each file
   * minutes after the press, by which time the reviewer is usually back in
   * the folder view and the live read answers `undefined` for every member.
   * Reading the freeze first is what makes this correct during a pass rather
   * than only for a single click.
   */
  const inMergedSet =
    !!frozen?.watermarkProjectTdoId ||
    !!(yield* select(selectWatermarkProjectFor(tdoId)));
  if (watermarkGovernance.status === 'unresolved') {
    /**
     * ONE RETRY, HERE, because `unresolved` is otherwise permanent.
     *
     * The record is read once when the editor mounts. A read that fails —
     * a dropped connection, a token refreshed mid-flight, a 502 from the
     * gateway — leaves the record `failed` and nothing ever asks again, so
     * for the rest of the session EVERY member of that release burns
     * unmarked, each with the warning below, and the only remedy is a page
     * reload nothing tells the reviewer to do.
     *
     * At the click rather than on a timer: this is the one moment the
     * answer is about to be written into a released document, and it is a
     * moment the reviewer is already waiting through.
     *
     * Called rather than dispatched, so this waits for the answer instead
     * of racing the watcher that would also pick the action up. It cannot
     * throw — the handler puts FAILURE and returns — and if it fails again
     * the governance is unchanged and the burn goes on to say so.
     *
     * Membership is NOT retried: it comes from the open request, and when
     * it is missing so is the project id this needs.
     */
    const projectTdoId = yield* select(selectWatermarkProjectFor(tdoId));
    if (projectTdoId) {
      yield* call(
        handleLoadProjectRedaction,
        LOAD_PROJECT_REDACTION_REQUEST({ projectTdoId })
      );
      watermarkGovernance = yield* select(selectWatermarkGovernanceFor(tdoId));
    }
  }

  /**
   * VE-26422: work out which version this burn-in will produce BEFORE saving,
   * so the save can be written already carrying it. That label is what the
   * staleness verdict compares against the finished output's `version`, and
   * writing it at creation rather than updating the asset afterwards means it
   * cannot half-fail — there is no window where the data is saved but unlabelled.
   *
   * `existing` is the prior outputs, snapshotted here and used for cleanup once
   * the new one lands. Nothing else creates redacted-document assets, so the
   * only one added in between is ours, which the cleanup already excludes.
   */
  let nextVersion: number;
  let existing: Array<{ id: string }>;
  try {
    const computed = yield* call(
      computeNextVersion,
      initialSession.tdoId,
      token,
      cfg
    );
    nextVersion = computed.nextVersion;
    existing = computed.existing;
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Export error';
    yield* failExport(
      tdoId,
      message,
      'Could not prepare the redaction. Please try again.'
    );
    return 'failed' as const;
  }

  yield* reportStage(tdoId, 'saving');
  const preSave = yield* call(
    saveBeforeExport,
    nextVersion,
    initialSession,
    unreviewed ? UNREVIEWED_INCLUDE_WRITER : undefined
  );
  if (!preSave.ok) {
    yield* failExport(
      tdoId,
      'Could not save redactions before exporting.',
      'Could not save redactions. Please try again.'
    );
    return 'failed' as const;
  }

  // Re-read the session now the save has settled. `initialSession` predates
  // both computeNextVersion's round-trip and the save above, so rendering
  // from it would drop any edit made in between and put the output out of
  // step with the asset just persisted. If the editor has been closed (or
  // shows another document) since the click, the store is no longer this
  // document's — render the snapshot the save persisted instead (VE-28061).
  //
  // EVERY consumer below must read `saved`, never `initialSession` and never
  // `selectSession` again. The detections path is the easy one to miss:
  // buildRedactionPlan sources all `origin: 'auto'` PII rects from
  // `session.detections` / `detectionRectOverrides`, and the editor stays
  // live during an export (isExporting only disables the Redact File button
  // itself).
  const liveSession = yield* select(selectSession);
  const saved = resolveExportSource(liveSession, preSave.snapshot);

  const { pdfUrl, documentName, redactions, pageRedacts, excludedPages } =
    saved;

  try {
    /**
     * ----- 1. Fetch the source PDF bytes -------------------------------
     *
     * THE UN-ENRICHED PDF, not the file the session opened against.
     *
     * OCR republishes its searchable copy as the TDO's primary `media` asset,
     * so after a reload `session.pdfUrl` IS that copy — and burning into it
     * would carry OCR's invisible text layer into the released document. The
     * geometry of the two files is identical, so every redaction lands the
     * same either way; what differs is what survives underneath.
     *
     * `applyRedactions` does delete text inside the boxes, so this is not a
     * plain leak. The risk is narrower and worse: OCR's glyph boxes are
     * INFERRED from the image, so a redaction drawn against the picture can
     * miss a point or two of the text box it is meant to cover, leaving a
     * fragment extractable just outside the removal rect. On the un-enriched
     * file that class of failure cannot happen, because there is no machine-
     * inferred text to leave behind. The output is then not searchable, which
     * is the accepted trade: minimise what the redacted file carries that the
     * original did not.
     *
     * Falls back to the session's URL if the lookup gives nothing — an export
     * that works on the file we have beats one that refuses to run.
     */
    let sourceUrl = pdfUrl;
    /**
     * Which asset the bytes came from, carried to `RedactedDocumentDetails` and
     * the audit log below so a released document can be traced back to the file
     * it was cut from without reasoning backwards from timestamps.
     */
    let sourceAssetId: string | null = null;
    let usedEnrichedFallback = false;
    try {
      const mediaAssets = yield* call(
        serviceFetchDocumentMediaAssets,
        tdoId,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      );
      const original = findPdfOriginalMediaAsset(
        mediaAssets,
        documentOcrEngineId
      );
      if (original?.signedUri) {
        sourceUrl = original.signedUri;
        sourceAssetId = original.id;
      } else {
        // Nothing could be shown to be the un-enriched file. Falling back is
        // still better than refusing to export, but it is the OUTCOME THIS
        // CHANGE EXISTS TO AVOID, so it is recorded rather than only warned.
        usedEnrichedFallback = true;
        sourceAssetId = saved.mediaAssetId ?? null;
      }
    } catch (e: unknown) {
      usedEnrichedFallback = true;
      sourceAssetId = saved.mediaAssetId ?? null;
      console.warn(
        'Could not resolve the un-enriched PDF; exporting from the open file.',
        e
      );
    }
    if (usedEnrichedFallback) {
      yield* put(
        enqueueSnackbar({
          message:
            'Could not identify the un-enriched original; redacting the file as opened.',
          variant: 'warning',
          autoHideDuration: 8000,
        })
      );
    }

    yield* reportStage(tdoId, 'fetching');
    const pdfRes = yield* call(fetch, sourceUrl);
    if (!pdfRes.ok) {
      throw new Error(`Failed to fetch source PDF: HTTP ${pdfRes.status}`);
    }
    const sourceBytes = yield* call([pdfRes, pdfRes.arrayBuffer]);

    // ----- 2. Burn in via the mupdf worker -------------------------------
    // Filter out `'page-redact'` origin redactions from the bounding-box
    // list — those are tracked separately in `session.pageRedacts` and
    // get expanded into full-page rects inside the worker (which knows
    // the real page dimensions via `page.getBounds()`).
    // Un-redacted (`r.redacted === false`) records are also excluded so the
    // exported PDF matches what the editor / rail / metrics show — the
    // model docstring and `buildRedactionPlan` both promise inactive
    // entries are skipped, but the burn-in path was reading raw
    // `session.redactions` and painting them anyway.
    yield* put(EXPORT_SUBMITTED({ tdoId, jobId: 'client-burn-in' }));
    const burnInCandidates = redactions.filter(
      (r) => r.origin !== 'page-redact' && r.redacted !== false
    );
    // VE-20838: "Stick to all pages" redactions get sent as a
    // separate `allPagesRedactions` list so the worker can expand
    // them using its own `doc.countPages()` (the editor's
    // session.totalPages is only a seed until pdfjs reports and can be
    // stale). Per-page redactions go in the regular list.
    const perPageRedactions = burnInCandidates.filter(
      (r) => !r.appliesToAllPages
    );
    const allPagesRedactions = burnInCandidates
      // Polygons go to `allPagesPolygons` below, which keeps their vertices;
      // sending one here would flatten it to its bounding box and redact more
      // of the page than the operator drew.
      .filter((r) => r.appliesToAllPages && !r.isCustomPolygon)
      .map((r) => ({
        x: r.geometry.x,
        y: r.geometry.y,
        width: r.geometry.width,
        height: r.geometry.height,
      }));
    // Checked PII detections burn in as black-filled rects too. They live
    // on `session.detections` (not `session.redactions`), so pull them from
    // the redaction plan — which expands each checked occurrence to a
    // per-page rect (overrides applied) tagged `origin: 'auto'` — and feed
    // them through the same rect path, carrying their redaction code.
    const detectionRects = buildRedactionPlan(saved)
      .items.filter((i) => i.origin === 'auto')
      .map((i) => ({
        page: i.page,
        x: i.geometry.x,
        y: i.geometry.y,
        width: i.geometry.width,
        height: i.geometry.height,
        code: i.redactionCode?.label,
        color: i.redactionCode?.color,
        // Lexicon detections are glyph-anchored (VE-24948) and land on this
        // `auto` path beside the PII-engine rows, which are not. Their box is
        // MuPDF's per-glyph quad — the full font LINE box — so removing
        // content under it un-inset deletes characters from the rows above
        // and below the match (review-gate B1). PII rows carry vendor boxes
        // and must keep inset 0, which is why this is per-item rather than
        // per-origin.
        removalInsetV: i.glyphAnchored
          ? GLYPH_REDACTION_REMOVAL_VINSET
          : undefined,
      }));
    // Neighbours for the visual pad below, grouped by page once. The helper
    // filters to the target's page itself, but `perPageRedactions` spans the
    // whole document, so handing it that list is O(n) per box and O(n²) per
    // export — ~0.6s of main-thread block at 10k keyword boxes, ~2.5s at 30k
    // (Node microbench), before the worker even got the request (VE-29483).
    // Same members, same order, so the pads match.
    const padNeighboursByPage = new Map<number, typeof perPageRedactions>();
    for (const r of perPageRedactions) {
      const onPage = padNeighboursByPage.get(r.page);
      if (onPage) {
        onPage.push(r);
      } else {
        padNeighboursByPage.set(r.page, [r]);
      }
    }
    // Rectangle redactions (black-filled). Polygons are handled separately
    // below so the worker can paint a crisp vector shape over their strips.
    const burnInRequest = [
      ...perPageRedactions
        .filter((r) => !r.isCustomPolygon)
        .map((r) => {
          // Glyph-anchored (keyword / text-select) boxes are stored NEUTRAL
          // (exact glyph bounds). The worker derives two boxes from it: the
          // removal box, inset vertically by `removalInsetV` so applyRedactions
          // can't grab the row above/below; and a roomier black fill grown by
          // the per-side visual pad (capped by the gap to the nearest adjacent
          // redaction so padded boxes never overlap, matching the editor).
          const glyphAnchored = isGlyphAnchoredRedaction(r);
          const pad = glyphAnchored
            ? glyphAnchoredVisualPadAmong(
                r,
                padNeighboursByPage.get(r.page) ?? []
              )
            : { top: 0, bottom: 0, left: 0, right: 0 };
          return {
            page: r.page,
            x: r.geometry.x,
            y: r.geometry.y,
            width: r.geometry.width,
            height: r.geometry.height,
            // Stamp the assigned redaction code (its short label) into the
            // box, drawn in the code's assigned display color.
            code: r.redactionCode?.label,
            color: r.redactionCode?.color,
            removalInsetV: glyphAnchored
              ? GLYPH_REDACTION_REMOVAL_VINSET
              : undefined,
            padTop: pad.top || undefined,
            padBottom: pad.bottom || undefined,
            padLeft: pad.left || undefined,
            padRight: pad.right || undefined,
          };
        }),
      ...detectionRects,
    ];
    // Free-form polygons: send the vertices (for the crisp vector overlay)
    // plus pre-decomposed scanline strips (for true content removal). The
    // worker removes content under the strips with no fill, then draws the
    // polygon shape in black on top — smooth edges, no staircase.
    const toBurnInPolygon = (r: (typeof burnInCandidates)[number]) => ({
      points: r.points as Array<{ x: number; y: number }>,
      strips: polygonToStrips(
        r.points as Array<{ x: number; y: number }>,
        POLYGON_STRIP_HEIGHT
      ),
      code: r.redactionCode?.label,
      color: r.redactionCode?.color,
    });
    const isDrawablePolygon = (r: (typeof burnInCandidates)[number]) =>
      !!r.isCustomPolygon && !!r.points && r.points.length >= 3;
    const polygons = perPageRedactions
      .filter(isDrawablePolygon)
      .map((r) => ({ page: r.page, ...toBurnInPolygon(r) }));
    // "Stick to all pages" polygons. Built off `burnInCandidates`, NOT
    // `perPageRedactions` — that list is everything the flag is false for, so
    // reading polygons out of it can only ever find the unstuck ones.
    //
    // This list exists because a stuck polygon used to match no list at all:
    // `polygons` could not see it (per-page) and `allPagesRedactions` excluded
    // it (rects only), so it burned in on NO page while the editor drew it on
    // every one, the filename still said [REDACTED], and the redaction report
    // listed it under page "All". The context menu offers "Stick to all pages"
    // on a polygon — it gates on `isGlyphAnchoredRedaction`, which only hides
    // keyword / text-select — so this is reachable in the product, and the
    // combination can already exist in saved documents.
    const allPagesPolygons = burnInCandidates
      .filter((r) => r.appliesToAllPages && isDrawablePolygon(r))
      .map(toBurnInPolygon);
    // VE-24693: map each applied watermark (AC#5) into the burn-in payload,
    // dropping any with nothing to draw (empty text / no image).
    //
    // Read off `saved`, like every other consumer here — since VE-28061 that is
    // the snapshot the pre-export save persisted, and it is the only source
    // that still answers once the editor has been closed mid-export.
    //
    // Gated on the flag HERE, not only on the buttons that add one. The flag is
    // the feature's rollback switch, and a switch that leaves marks already
    // written into a document's asset burning into exported evidence is not
    // one: turning it off has to stop the output changing, which is the only
    // reason anyone would reach for it.
    const { enableWatermarks, enableBatesStamping } =
      yield* select(selectFeatureFlags);
    /**
     * THE SET'S MARKS FOR A MEMBER, the document's own otherwise (VE-30104).
     *
     * A member of a merged document keeps no marks of its own — they live on
     * the project and are resolved per render — so reading `saved.watermarks`
     * here burned whatever that file happened to carry from before it joined,
     * at that mark's old position and size, while the page showed the set's.
     *
     * `unresolved` BURNS NONE OF THE SET'S rather than falling back to them,
     * and records no provenance, so the output reads `behind`. A released
     * document marked with the wrong thing is worse than one marked with
     * nothing — the same choice the Bates resolver makes for an uncomputable
     * start. The file's own marks still burn: they are its own.
     *
     * Switched off: the marks are kept and burn nothing, exactly as the page
     * draws nothing. The SET has its own switch, already applied by the
     * resolver; `saved.watermarksOff` governs only a file's own marks.
     */
    /**
     * THE SHARED RULE, which the page calls too — see `watermarksToDraw`.
     * A rule about what reaches a released record gets one home: a second
     * copy here is free to disagree with the page, and what the page shows
     * is the reviewer's only check on what ships.
     */
    const sourceWatermarks: readonly AppliedWatermark[] = watermarksToDraw({
      governance: watermarkGovernance,
      ownMarks: saved.watermarks ?? [],
      ownWatermarksOff: saved.watermarksOff ?? false,
    });
    // NOT WITH THE FEATURE OFF. Nothing was going to be drawn either way, so
    // a warning that the merged document's watermarks could not be read
    // describes a loss the reviewer was never going to have.
    if (enableWatermarks && watermarkGovernance.status === 'unresolved') {
      yield* put(
        enqueueSnackbar({
          message: sagaIntl().formatMessage({
            id: 'watermarkSetUnresolvedBurn',
            defaultMessage:
              'The merged document’s watermarks could not be read, so this file was redacted without them.',
          }),
          variant: 'warning',
          autoHideDuration: 10000,
        })
      );
    }
    // Converted ONCE and kept beside the mark it came from, so the check
    // below asks about the payload that actually reaches the worker rather
    // than re-deriving drawability from a second copy of the rule.
    const convertedWatermarks = sourceWatermarks.map((mark) => ({
      mark,
      burn: toBurnInWatermark(mark),
    }));
    const watermarks = enableWatermarks
      ? convertedWatermarks
          .map((c) => c.burn)
          .filter((w): w is BurnInWatermark => w !== null)
      : [];
    /**
     * DID EVERY MARK THE SET OWES THIS FILE ACTUALLY SURVIVE TO THE PAYLOAD?
     *
     * `toBurnInWatermark` returns null for an image mark with no drawn raster,
     * which is what a failed hydration, a deleted asset or a refused image
     * produces. The mark is then silently absent from the burn.
     */
    const governedIds = new Set(
      watermarkGovernance.status === 'governed'
        ? watermarkGovernance.marks.map((w) => w.instanceId)
        : []
    );
    const governedMarkDropped = convertedWatermarks.some(
      (c) => governedIds.has(c.mark.instanceId) && c.burn === null
    );
    /**
     * THE SET'S VERSION, recorded only when its marks were the ones drawn,
     * all of them reached the page, and the asset they came from is known.
     *
     * EVERY CLAUSE IS LOAD-BEARING; the condition is not as redundant as it
     * looks. Status alone would let an output claim the set's version when
     * the marks dropped and nothing burned — and the staleness check, reading
     * that claim, would agree. The asset id has to be known because that is
     * what staleness actually compares: a version without one reads as
     * carrying NONE of the set's marking, so a release still asserting a
     * withdrawn designation goes unflagged. It can be null — between an
     * optimistic write and its confirmation the record holds `assetId: null`
     * while the status is still `governed`.
     */
    const governingProjectWatermarks =
      enableWatermarks &&
      watermarkGovernance.status === 'governed' &&
      watermarkGovernance.modifiedDateTime !== null &&
      !governedMarkDropped
        ? watermarkGovernance.modifiedDateTime
        : null;
    if (governedMarkDropped) {
      // SAID OUT LOUD, because the consequence is invisible and permanent:
      // the file is released without marking the release is supposed to
      // carry, and with no version recorded it reads as `behind` rather than
      // as verified — which is the honest state, and the one the reviewer can
      // act on.
      yield* put(
        enqueueSnackbar({
          message: sagaIntl().formatMessage({
            id: 'watermarkArtworkMissingBurn',
            defaultMessage:
              'Some of the merged document’s watermarks could not be drawn, so this file was redacted without them.',
          }),
          variant: 'warning',
          autoHideDuration: 10000,
        })
      );
    }
    // The Bates stamp rides in the same array as the decorative marks. Appended
    // last for readability only — the WORKER puts it last regardless
    // (`orderMarksForDraw`), because "a watermark never sits over the
    // identifier" must not depend on every producer remembering to concatenate
    // in the right order. Same flag discipline as the watermarks above: turning
    // the flag off has to stop the output changing, not merely hide the dialog.
    //
    // A MEMBER OF A MERGED DOCUMENT TAKES THE SET'S STAMP (VE-30105),
    // resolved at the click above rather than here.
    //
    // A MEMBER WHOSE STAMP CANNOT BE RESOLVED BURNS UNSTAMPED, never with its
    // own leftover per-file stamp: that stamp is not the set's and can land on
    // numbers another member already drew.
    const governing =
      batesGovernance.status === 'governed' ? batesGovernance.bates : null;
    const batesSettings =
      governing?.settings ??
      (batesGovernance.status === 'ungoverned' ? saved.batesStamp : null);
    /**
     * ----- 2b. Do the bytes and the coordinates agree? -------------------
     *
     * The bytes come from an asset this saga resolved; every rect comes from
     * the session, built against a different file. Nothing checked they
     * correspond, and the worker's out-of-range handling is `continue`, not
     * throw — so a shorter source SILENTLY DROPS the redactions past its end
     * while the export succeeds, takes the `[REDACTED]` name, bumps the version
     * and publishes. Under-redacted evidence, released, with no error anywhere.
     *
     * MEASURED FROM THE PDF, and compared against the pages the redactions
     * actually name. The first attempt at this asserted `session.totalPages`,
     * which is seeded at 1 and never corrected, and it blocked every export
     * until it was reverted (59275cd9). A count read from the source's own
     * catalog cannot be wrong that way, and "is there a page 40 to redact?" is
     * the question that matters rather than "do the two files have equal
     * length" — two documents can differ in page count for reasons that touch
     * nothing the operator drew.
     *
     * An unreadable count is not a mismatch: refusing an export because pdf.js
     * declined a file MuPDF will happily burn is the failure this is trying to
     * avoid, not cause.
     */
    let burnBytes = sourceBytes;
    // Not `Math.max(...)`: spreading every page into one call throws
    // RangeError on a large enough document (VE-29483).
    const highestRedactedPage = highestPageOf(
      burnInCandidates.map((r) => r.page),
      pageRedacts,
      excludedPages,
      detectionRects.map((d) => d.page)
    );
    const sourcePageCount = yield* call(readPdfPageCount, sourceBytes);
    if (sourcePageCount !== null && highestRedactedPage > sourcePageCount) {
      const mismatch =
        `source PDF has ${sourcePageCount} page(s) but a redaction targets ` +
        `page ${highestRedactedPage}`;
      if (sourceUrl === pdfUrl) {
        // No swap happened, so the coordinates exceed the very file they were
        // drawn on and there is nothing better to fall back to. Failing is the
        // only honest exit: publishing would ship the missing boxes silently.
        throw new Error(
          `Redactions do not fit the source document: ${mismatch}`
        );
      }
      // The resolved original is too short for work drawn against the file the
      // editor had open. Burn what the operator actually saw rather than
      // dropping their boxes — the provenance below records that we did.
      console.warn(
        `Un-enriched source rejected (${mismatch}); exporting from the open file.`
      );
      const openRes = yield* call(fetch, pdfUrl);
      if (!openRes.ok) {
        throw new Error(`Failed to fetch source PDF: HTTP ${openRes.status}`);
      }
      burnBytes = yield* call([openRes, openRes.arrayBuffer]);
      sourceUrl = pdfUrl;
      sourceAssetId = saved.mediaAssetId ?? null;
      usedEnrichedFallback = true;
      yield* put(
        enqueueSnackbar({
          message:
            'The original file did not match this document; redacting the file as opened.',
          variant: 'warning',
          autoHideDuration: 8000,
        })
      );
    }

    /**
     * WILL THIS BURN DRAW PAST THE SPAN THE SEQUENCE ALLOCATED IT?
     *
     * The rule and the reasoning live in `batesSpanOverrun`, beside the
     * arithmetic that hands out the spans. Only an OVERRUN is a defect — see
     * there for why an undercount and an unknown allocation are not, and why
     * an unmeasurable file still is (VE-31894).
     *
     * CHECKED AGAINST `burnBytes`, BELOW THE SWAP. The fit guard above may
     * replace the source with the file the editor had open, and that file can
     * have a different length — measuring before the swap compared the span
     * against bytes that were then discarded.
     */
    const burnPageCount =
      burnBytes === sourceBytes
        ? sourcePageCount
        : yield* call(readPdfPageCount, burnBytes);
    const batesSpanMismatch =
      governing !== null &&
      batesSpanOverrun({
        assumedPageCount: governing.assumedPageCount,
        burnPageCount,
      });
    if (batesSpanMismatch) {
      yield* put(
        enqueueSnackbar({
          message: sagaIntl().formatMessage({
            id: 'batesSpanMismatch',
            defaultMessage:
              'This file was redacted without Bates numbering: its recorded page count does not match the document.',
          }),
          variant: 'warning',
        })
      );
    }
    const batesStamp =
      enableBatesStamping && batesSettings && !batesSpanMismatch
        ? toBurnInBatesStamp(batesSettings)
        : null;
    // Recorded only when the project's stamping actually governed AND a mark
    // was actually drawn — with the flag off the output carries no numbers,
    // so claiming it carries the set's would be false.
    const governingProjectBates = batesStamp
      ? (governing?.projectModifiedDateTime ?? null)
      : null;

    /**
     * The readable half of the provenance record, beside the durable one on the
     * asset's own details.
     *
     * Emitted HERE — before the burn, after the source is settled — because the
     * audit CSV is assembled from the platform's events immediately after the
     * burn finishes, and `LOG_AUDIT_EVENT` is fire-and-forget. Putting the whole
     * burn-in between the emit and the read is what gets this event into the
     * CSV describing the version it is about, rather than the next one.
     *
     * `tdoId` is passed explicitly: this runs after a save round-trip and can
     * outlive a document switch, so the emitter must not resolve "which
     * document" from whatever is on screen.
     */
    yield* put(
      LOG_AUDIT_EVENT({
        tdoId,
        /**
         * AN UNREVIEWED RELEASE SAYS SO, in the action rather than buried in
         * the details, because the action is the column a reader scans
         * (VE-32440). Without it the audit CSV of a file nobody opened was
         * word-for-word the CSV of a file somebody read end to end and found
         * nothing in — the one distinction an auditor or a FOIA appeal most
         * needs, and the only durable record of a decision the reviewer made
         * once in a dialog.
         */
        action: unreviewed
          ? 'Redacting without review — included in the merged PDF on request'
          : usedEnrichedFallback
            ? 'Redacting from the file as opened'
            : 'Redacting from the original uploaded file',
        payload: {
          details: [
            unreviewed
              ? 'no redactions applied; nobody has reviewed this file'
              : null,
            usedEnrichedFallback
              ? 'the un-enriched original could not be identified'
              : null,
            sourceAssetId ? `media asset ${sourceAssetId}` : 'asset id unknown',
            sourcePageCount !== null ? `${sourcePageCount} pages` : null,
          ]
            .filter(Boolean)
            .join(' · '),
        },
      })
    );

    /**
     * THE BURN, WITH A LINE OPEN BACK TO THE STORE (VE-31309).
     *
     * The worker walked several hundred pages in silence; it reports as it
     * goes now, and the pump turns those reports into actions. Everything
     * here is advisory — a dropped report costs nothing and the burn is
     * unchanged if nobody listens — so none of it is allowed to fail the job.
     */
    let emitProgress: ((report: BurnInProgressReport) => void) | null = null;
    const progressChannel = makeBurnProgressChannel((emit) => {
      emitProgress = emit;
    });
    yield* fork(pumpBurnProgress, tdoId, progressChannel);
    let result: BurnInResult;
    try {
      result = yield* call(burnInPdf, {
        pdfBytes: burnBytes,
        redactions: burnInRequest,
        pageRedacts,
        excludedPages,
        allPagesRedactions,
        polygons,
        allPagesPolygons,
        watermarks: batesStamp ? [...watermarks, batesStamp] : watermarks,
        onProgress: (report: BurnInProgressReport) => emitProgress?.(report),
      });
    } finally {
      // The WORKER has stopped reporting — closing terminates the pump, since
      // a taker on a closed channel is ended. NOT the end of the file's
      // progress, though: the upload is still to come and reports a stage of
      // its own, so the done marker belongs at the end of the whole burn and
      // not here. It sat here first, which left `burnProgress` holding
      // "Uploading" for a file that had finished.
      progressChannel.close();
    }
    const redactedBytes = new Uint8Array(result.pdfBytes);

    // ----- 3. Filename (the version was computed before the save) --------
    // "Has redactions" must include the page-level redactions (`pageRedacts`)
    // and any hidden pages — otherwise a session with only "Remove
    // Page" or "Delete Page" usage would be tagged
    // `[NO_REDACTIONS]` even though the output PDF is materially
    // different from the source.
    /**
     * NOT A CLAIM AN UNREVIEWED RELEASE MAY MAKE.
     *
     * `[NO_REDACTIONS]` is the filename's way of saying a reviewer looked and
     * found nothing — `filename.ts` puts it plainly: "a claim about the whole
     * release, and a claim nobody can check must not be made". An included
     * unreviewed file has an empty plan for the opposite reason, so measuring
     * the plan is the wrong test for it (VE-32440).
     */
    const hasRedactions =
      unreviewed ||
      redactions.length > 0 ||
      detectionRects.length > 0 ||
      pageRedacts.length > 0 ||
      excludedPages.length > 0;
    const filename = buildRedactedFilename(
      documentName,
      hasRedactions,
      nextVersion
    );
    // Count manual-origin redactions for the metrics block. burnInInputs
    // (above) already drops 'page-redact'; counting from the full
    // `redactions` list with an explicit `'manual'` filter is the metric
    // we want (user-drawn bounding boxes only — keyword/manual/text-
    // select are distinct in the UI and we only want manual here).
    // VE-20839: exclude un-redacted records so the metrics reflect
    // what the burn-in actually applied.
    const manualCount = redactions.filter(
      (r) => r.origin === 'manual' && r.redacted !== false
    ).length;
    // Per-category word/match metrics (text-select rows/words/spans, keyword
    // unique/total/words, PII unique/total/words). Computed from the SAVED
    // session (the post-save re-read) so the metrics describe the same state
    // the burn-in rendered — redacted records only.
    const redactionMetrics = computeRedactionMetrics(saved);
    const details: RedactedDocumentDetails = {
      version: nextVersion,
      assetSize: redactedBytes.byteLength,
      numPages: result.numPages,
      // The ticket lists startPage/endPage in the details schema. The
      // burn-in always covers the full document, so they're 1..numPages.
      startPage: 1,
      endPage: result.numPages,
      // Which file this was cut from. The durable half of the record — the
      // audit event below is the readable one, but it lives in a CSV that
      // rotates, while this rides on the released asset itself.
      sourceMediaAssetId: sourceAssetId,
      sourceIsEnrichedFallback: usedEnrichedFallback,
      ...(governingProjectBates !== null && governing
        ? {
            batesProjectModifiedDateTime: governingProjectBates,
            batesStartNumber: governing.startNumber,
          }
        : {}),
      // VE-30104: WHEN the set's marks these pages carry last changed, so the
      // staleness check can tell an output marked under the current settings
      // from one marked under older ones. One stamp, not a version beside an
      // asset id — see `projectRedactionAsset`'s header.
      ...(governingProjectWatermarks !== null
        ? { watermarkProjectModifiedDateTime: governingProjectWatermarks }
        : {}),
      // Burned deliberately unnumbered because the span did not match. Without
      // this the output looks merely behind, and the badge offers a rebuild
      // that declines again for the same reason — an amber loop whose only
      // explanation was a snackbar that is long gone.
      ...(batesSpanMismatch ? { batesSpanMismatch: true } : {}),
      metrics: {
        manual: { count: manualCount },
        textSelect: redactionMetrics.textSelect,
        keywords: redactionMetrics.keywords,
        PII: redactionMetrics.PII,
        downloads: 0,
        // Persist page-level counts on the asset so the Redacted File tab
        // shows the right numbers on reopen (not just right after burn-in).
        pagesRemoved: excludedPages.length,
        pagesFullyRedacted: pageRedacts.filter(
          (p) => !excludedPages.includes(p)
        ).length,
      },
      /**
       * Nothing was redacted, and a person said that was correct.
       *
       * READ IT WITH `includedUnreviewed` (VE-32440) — on its own it no
       * longer carries that second half. A file released without review
       * also has nothing redacted in it, and the two mean opposite things
       * about how much the output can be trusted: one is a reviewer's
       * verdict, the other is the absence of one.
       */
      hasNoRedactions:
        manualCount === 0 &&
        excludedPages.length === 0 &&
        pageRedacts.length === 0 &&
        redactionMetrics.textSelect.wordCount === 0 &&
        redactionMetrics.keywords.wordCount === 0 &&
        redactionMetrics.PII.wordCount === 0,
      /**
       * NOBODY READ THIS FILE, recorded on the artifact itself.
       *
       * The audit CSV says it too, but a CSV is rotated per version and read
       * by a person who went looking; this travels with the output for as
       * long as the output exists, and is what lets any later surface —
       * download review, a released-set report, a support question two years
       * on — tell an unreviewed release from a reviewed one. Written only
       * when true, so every existing asset keeps its meaning.
       */
      ...(unreviewed ? { includedUnreviewed: true } : {}),
    };

    // ----- 4. Upload as a new redacted-document asset --------------------
    yield* reportStage(tdoId, 'uploading');
    const created = yield* call(
      serviceSaveRedactedDocumentAsset,
      tdoId,
      redactedBytes,
      filename,
      details,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );

    // ----- 4b. Build + upload the audit-log CSV asset --------------------
    // Gather every `redact-document` audit event for this TDO into a CSV
    // `redact-audit-log` asset linked to the just-created redacted-document
    // asset (mirrors imageRedactionSaga). Best-effort: the burn-in already
    // succeeded, so a failure here must not surface as an export error — the
    // download button just won't appear for this version.
    let auditLogUrl: string | null = null;
    let newAuditAssetId: string | null = null;
    let newAuditAsset: { id: string; name: string; signedUri: string } | null =
      null;
    try {
      // saveAuditLogAsset / callGraphQLApi read config + token from a
      // getState accessor. Hand them the store as it was when Redact File was
      // clicked, so the audit-log asset — the record of WHO redacted this
      // version — is created by the principal who started the burn-in, not by
      // whoever is signed in when it finishes.
      const auditAsset = yield* call(
        uploadDocumentAuditLog,
        tdoId,
        created.id,
        nextVersion,
        filename,
        () => stateAtClick
      );
      auditLogUrl = auditAsset?.signedUri ?? null;
      newAuditAssetId = auditAsset?.id ?? null;
      newAuditAsset = auditAsset
        ? {
            id: auditAsset.id,
            name: auditAsset.name,
            signedUri: auditAsset.signedUri,
          }
        : null;
    } catch (e) {
      console.warn('Audit-log asset creation failed (non-fatal):', e);
    }

    // ----- 5. Update TDO's latest-version cache + rotate old assets ------
    // Both are best-effort: the user already has a successful burn-in by
    // this point, so a transient failure here shouldn't surface as an
    // export error.
    try {
      yield* call(
        serviceUpdateTdoLatestRedactedDocumentVersion,
        tdoId,
        nextVersion,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      );
    } catch (e) {
      console.warn(
        'Failed to update TDO latestRedactedDocumentVersion (non-fatal):',
        e
      );
    }
    yield* call(
      removeOlderRedactedDocumentAssets,
      existing,
      created.id,
      token,
      cfg
    );
    // Rotate the audit-log CSVs at the same rate — keep only the one linked
    // to this burn-in, delete all prior. (No-op if the audit asset failed to
    // create above.)
    if (newAuditAssetId) {
      yield* call(
        removeOlderAuditLogAssets,
        tdoId,
        newAuditAssetId,
        token,
        cfg
      );
    }

    // ----- 6. Surface success in redux + flip to the redacted-file tab ---
    // The reducer applies EXPORT_COMPLETED only to a session showing this
    // TDO, so a burn-in finishing after the editor closed (or after another
    // document was opened) cannot rewrite the wrong versionHistory.
    const sessionNow = yield* select(selectSession);
    const stillOpen = sessionNow?.tdoId === tdoId;
    // BEFORE the completion, which clears it. See the snackbar below.
    const interruptedDuringBurn = stillOpen
      ? yield* select(selectExportInterrupted)
      : undefined;
    // Membership was captured at the click (see `inMergedSet` above): by here
    // the open request may be gone, and the answer with it.
    yield* put(
      EXPORT_COMPLETED({
        tdoId,
        inMergedSet: inMergedSet,
        includedUnreviewed: !!unreviewed,
        // What was actually written, for the row that has to mirror it.
        details,
        versionId: created.id,
        version: nextVersion,
        redactedAssetUrl: created.signedUri,
        name: created.name ?? filename,
        assetSize: redactedBytes.byteLength,
        numPages: result.numPages,
        // Display value is "# Redactions"; align it with the metric we
        // persist (manual count) so the reopen path and the just-burned-in
        // path show the same number.
        redactionCount: manualCount,
        textSelect: redactionMetrics.textSelect,
        keywords: redactionMetrics.keywords,
        pii: redactionMetrics.PII,
        pagesRemoved: excludedPages.length,
        batesProjectModifiedDateTime: governingProjectBates,
        batesStartNumber: governingProjectBates
          ? (governing?.startNumber ?? null)
          : null,
        batesSpanMismatch,
        watermarkProjectModifiedDateTime: governingProjectWatermarks,
        // Pages flagged for full-page redaction that still ship (excluded
        // pages are dropped, so they don't count here).
        pagesFullyRedacted: pageRedacts.filter(
          (p) => !excludedPages.includes(p)
        ).length,
        auditLogUrl,
        createdDateTime: created.createdDateTime,
        auditLogAsset: newAuditAsset,
      })
    );
    // The Documents panel's marker reads the casemedia row for this file, and
    // nothing on the editor route refreshes those rows (VE-30011). The casemedia
    // reducer records the new output from EXPORT_COMPLETED itself; this asks
    // the server for the rest — redaction-data timestamps for the staleness
    // check, the real asset record — through the reducer that is mounted on
    // every route. The thunk, not `actionRefreshFileOutputs`: that action's
    // watcher lives in `caseMediaSaga`, which the editors cancel.
    yield* put(fetchFileOutputs([tdoId]));
    // Neither branch releases the "Redacting" indicator: the watcher's
    // `finally` does, once, on every exit.
    /**
     * SOMETHING ALWAYS SAYS IT FINISHED.
     *
     * With the document still on screen the reducer normally answers: it
     * flips to the Redacted Files tab, or leaves the strip reading complete.
     * Both are gone for a reviewer who kept editing while the burn ran — the
     * edit dismissed the strip on purpose, and the flip is withheld because
     * the output predates them — and the modal that used to cover exactly
     * that case was removed. The result was a burn finishing in silence on a
     * legal-release product, with a stopping spinner as the only cue.
     *
     * So this is the surface for that cohort, and ONLY for it. Which is why
     * it reads `interrupted` and not the strip's `notice`: null notice also
     * means the reviewer dismissed the strip, and also means no burn was
     * ever tracked — a Redact All pass sets none — so keying on it claimed
     * "your changes are missing" to a reviewer who had merely closed a
     * banner, and to every pass, about a file burned from their own latest
     * work. `undefined` is left distinct from `false` for that reason.
     *
     * Read BEFORE the completion is dispatched, because that dispatch
     * clears the flag so the next burn starts from nothing.
     */
    if (stillOpen && interruptedDuringBurn === true) {
      yield* put(
        enqueueSnackbar({
          message: sagaIntl().formatMessage({
            id: 'documentRedactionFinishedWhileEditing',
            defaultMessage:
              'Redacted file created. It does not include your latest changes.',
          }),
        })
      );
    }
    if (!stillOpen) {
      // Detached completion (VE-28061): the app-wide "a redaction finished"
      // event shows the success snackbar and refreshes the surfaces listing
      // this file — the gallery tile via mainPage and the folder row via
      // casemedia.
      // QUIET DURING A PASS. `burnOneDocument` is one file of however many,
      // and a Redact All run says its own piece when it is done.
      const bulkRun = yield* select(selectRedactAllRun);
      yield* put(
        REDACTED_FILE_COMPLETED({
          tdoId,
          documentName,
          quiet: !!bulkRun && bulkRun.phase !== 'done',
        })
      );
    }
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Export error';
    // Log the real cause: the toast below is intentionally generic, so
    // without this the burn-in worker's stage/page-tagged message (e.g.
    // "applyRedactions (page 7): ...") never reaches the console.
    console.error('[redact] redact-file/export failed:', message, e);
    // A watermark character no font can draw fails EVERY attempt, so "try
    // again" would be wrong advice; say what is wrong and where to fix it
    // (VE-30011). Only reachable from a library entry that predates the
    // drawer's character gate or was written outside it.
    const undrawable = parseUndrawableWatermarkError(message);
    yield* failExport(
      tdoId,
      message,
      undrawable
        ? `Could not redact document: a watermark on it contains a character that cannot be burned in (${undrawable}). Remove that watermark, or edit it in the Watermark Library, then redact again.`
        : 'Could not redact document. Please try again.'
    );
    return 'failed' as const;
  }
  // Reached only by falling off the end of the try, which is the one path
  // that published something.
  return 'done' as const;
}

/**
 * One EXPORT_REQUEST, start to finish. Owns the cross-app "Redacting"
 * indicator: lights it before the burn-in, refuses a second click while it is
 * lit, and releases it in `finally` on EVERY exit — success, failure, a throw
 * that escaped the burn-in, cancellation, and logout. It is a LOCK, not a
 * spinner (the refusal above and every list row read it), so a missed release
 * means Redact File permanently disabled for the document and every row stuck
 * on "Redacting" for the life of the tab. `REDACT_FILE_COMPLETED` is
 * idempotent, so releasing here as well as via the completion event is safe.
 *
 * The run is bounded by LOGOUT (VE-28061). The saga outlives the editor by
 * design, but it must not outlive the login: on a shared workstation the next
 * user would otherwise inherit the previous user's burn-in — its uploads, its
 * success toast, and (were it not for `stateAtClick`) its audit-log asset.
 * Losing the race drops the snapshot; the labelled redaction data is already
 * persisted, so the file simply reads "output behind" until redacted again.
 *
 * Nothing thrown inside may escape: this worker is forked by `takeEvery`, and
 * an uncaught error there aborts the whole `documentBurnInSaga`, which
 * `keepAlive` restarts — cancelling every OTHER burn-in in flight.
 */
/**
 * What became of one document's burn-in, for a caller running several.
 *
 * `handleExportRequest` discards this — a single click reports itself through
 * the indicator and its own toasts. A Redact All pass (VE-31006) counts them:
 * it has to know how many files it actually burned, and it must stop dead on
 * `loggedOut` rather than carry on signing the next nineteen uploads as a user
 * who is no longer there.
 */
export type BurnOutcome = 'done' | 'alreadyRunning' | 'failed' | 'loggedOut';

/**
 * Did the burn produce an output?
 *
 * `runDocumentBurnIn` is deliberately built NEVER TO THROW — it is forked by
 * `takeEvery`, and an uncaught error there would abort the whole root saga
 * and cancel every other burn in flight. Which meant every failure exit
 * returned normally, and a caller could not tell a published file from one
 * that never rendered. For a single click that cost nothing: the toast and
 * the indicator already said so. For a pass it was the whole failure policy
 * — "merge only over a complete run" compared a list that could never have
 * anything in it.
 */
export type BurnVerdict = 'done' | 'failed';

/**
 * ONE DOCUMENT, SAFELY, whoever asked.
 *
 * This is the envelope around `runDocumentBurnIn` rather than the burn: the
 * concurrency refusal, the cross-app indicator lock, the LOGOUT bound and the
 * guaranteed release. Extracted for VE-31006, where a pass over twenty files
 * needs every one of those per file — and a second copy of them would be a
 * second answer to "can this document be burned right now", which is the kind
 * of duplication that has already cost this feature three review rounds.
 *
 * It owns the cross-app "Redacting" indicator: lights it before the burn-in,
 * refuses a second run while it is lit, and releases it in `finally` on EVERY
 * exit — success, failure, a throw that escaped the burn-in, cancellation and
 * logout. It is a LOCK, not a spinner (the refusal here and every list row
 * read it), so a missed release means Redact File permanently disabled for
 * that document and every row stuck on "Redacting" for the life of the tab.
 * `REDACT_FILE_COMPLETED` is idempotent, so releasing here as well as via the
 * completion event is safe.
 *
 * Nothing thrown inside may escape: the single-click caller is forked by
 * `takeEvery`, and an uncaught error there aborts the whole
 * `documentBurnInSaga`, which `keepAlive` restarts — cancelling every OTHER
 * burn-in in flight.
 */
export function* burnOneDocument(
  tdoId: TDOId,
  /**
   * Predates `computeNextVersion`'s round trip AND the forced save, so it is
   * NOT what the burn-in renders from — see the re-read after the save
   * (VE-25718). For a document that is not open this is a headless snapshot
   * built from the saved asset; `saveBeforeExport` and `resolveExportSource`
   * both prefer the live session when one is showing this same document, so
   * passing it costs the open case nothing.
   */
  initialSession: SessionState,
  /** Passed straight through — see `runDocumentBurnIn`. */
  frozen?: FrozenGovernance,
  /** Passed straight through — see `runDocumentBurnIn`. */
  unreviewed?: boolean
): Generator<any, BurnOutcome, any> {
  // VE-28061: a burn-in for this document may still be running from an
  // earlier editor session — it survives CLOSE_DOCUMENT now, and a re-opened
  // editor starts with a fresh `export.phase`. Two concurrent burn-ins would
  // race on the version number and on the asset rotation, so refuse the
  // second. The editor also shows the button as busy off the same signal.
  const redactFiles = yield* select(selectRedactFiles);
  if (redactFiles[tdoId]?.status === 'running') {
    // SAID BY THE CALLER, not here. One click deserves "this document is
    // already being redacted"; a pass over twenty files deserves one summary,
    // not twenty toasts.
    return 'alreadyRunning';
  }
  // Light the cross-app "Redacting" indicator (folder row, case-media drawer,
  // gallery tile) immediately — mirrors the image burn-in.
  yield* put(REDACTING_FILE({ tdoId }));

  try {
    const { done, logout } = yield* race({
      done: call(runDocumentBurnIn, tdoId, initialSession, frozen, unreviewed),
      logout: take(LOGOUT),
    });
    if (logout) {
      console.warn(
        '[redact] redact-file/export abandoned: user logged out mid-burn-in',
        tdoId
      );
      // Keep the (possibly still open) session's export phase consistent; no
      // toast — there is nobody signed in to read it.
      yield* put(
        EXPORT_FAILED({
          tdoId,
          message: 'Logged out before the redaction finished.',
        })
      );
      return 'loggedOut';
    }
    // THE BURN'S OWN VERDICT, not the absence of a throw. `runDocumentBurnIn`
    // is built never to throw, so every failure came back here looking
    // exactly like a success — which made `'failed'` unreachable and turned
    // the pass's "merge only over a complete run" rule into a comparison
    // against a list that could never have anything in it.
    if (done === 'failed') {
      return 'failed';
    }
  } catch (e: unknown) {
    // The burn-in reports its own failures; this catches what it could not —
    // a reducer throwing on one of its puts, for instance.
    const message = e instanceof Error ? e.message : 'Export error';
    console.error('[redact] redact-file/export crashed:', message, e);
    yield* failExport(
      tdoId,
      message,
      'Could not redact document. Please try again.'
    );
    return 'failed';
  } finally {
    yield* put(redactFileCompleted(tdoId));
    /**
     * AND THE FILE'S PROGRESS, in the same breath as the indicator.
     *
     * It was dispatched from inside `runDocumentBurnIn`, at the end of the
     * burn — which is neither every exit nor the end of the file: the UPLOAD
     * still follows and reports a stage of its own, so `burnProgress` was
     * left holding "Uploading" for a file that had finished. Scattering it
     * across that function's exits would have missed this one anyway: when
     * LOGOUT wins the race above, the burn is CANCELLED and none of its
     * returns run. A finally does.
     */
    yield* put(DOCUMENT_BURN_PROGRESS_DONE({ tdoId }));
  }
  return 'done';
}

/**
 * One EXPORT_REQUEST — the open document, by the reviewer's own click.
 */
function* handleExportRequest() {
  const initialSession = yield* select(selectSession);
  if (!initialSession) {
    return;
  }
  const outcome = yield* call(
    burnOneDocument,
    initialSession.tdoId,
    initialSession
  );
  if (outcome === 'alreadyRunning') {
    yield* put(
      enqueueSnackbar({
        message: 'This document is already being redacted.',
        variant: 'info',
      })
    );
  }
}

export function* onExportRequest() {
  yield* takeEvery(EXPORT_REQUEST, handleExportRequest);
}

/**
 * Root saga that OWNS the detached document burn-in (VE-28061).
 *
 * Registered in `src/common-app/state/sagas/index.ts` as its own `keepAlive`
 * entry, deliberately NOT under `documentsSaga`: that root forks twenty
 * children under a bare `all`, so a throw in any of them (keyword search, the
 * PDF pre-passes, PII detection, the send-to integrations, ...) aborts the
 * `all`, `keepAlive` restarts the root, and every attached child — including
 * a multi-minute burn-in whose labelled redaction data is already persisted —
 * is cancelled with no EXPORT_FAILED and no toast. The whole point of the
 * detached run is that it finishes; it cannot depend on siblings never
 * throwing. Here the only thing that can take it down is itself, and
 * `handleExportRequest` swallows everything a run can throw.
 */
export function* documentBurnInSaga() {
  yield* all([fork(onExportRequest)]);
}

export function* onDeleteRedactedDocumentRequest() {
  yield* takeEvery(
    DELETE_REDACTED_DOCUMENT_REQUEST,
    function* ({ payload: { versionId } }) {
      const session = yield* select(selectSession);
      if (!session) {
        return;
      }
      const token: string = yield* select(selectToken);
      const cfg = yield* select(selectGraphQLConfig);

      try {
        // `serviceDeleteAsset` now throws on both GraphQL-level and
        // transport failures (rotation sagas wrap their calls in try/catch
        // to keep cleanup non-fatal). For user-initiated deletes we want
        // a clear failure path: dispatch SUCCESS only after the call
        // resolves; on throw, fall through to FAILURE + snackbar.
        yield* call(
          serviceDeleteAsset,
          versionId,
          token,
          cfg.apiRoot,
          cfg.graphQLEndpoint,
          cfg.veritoneAppId
        );
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Delete failed';
        yield* put(DELETE_REDACTED_DOCUMENT_FAILURE({ versionId, message }));
        yield* put(
          enqueueSnackbar({
            message: 'Could not delete redacted file. Please try again.',
          })
        );
        return;
      }
      yield* put(
        DELETE_REDACTED_DOCUMENT_SUCCESS({ versionId, tdoId: session.tdoId })
      );
      // The deleted redacted-document asset had a linked audit-log CSV — drop
      // it too so audit logs rotate with the media they describe instead of
      // orphaning. Best-effort: the user-facing delete already succeeded.
      try {
        const auditAssets = yield* call(
          serviceListAuditLogAssets,
          session.tdoId,
          token,
          cfg.apiRoot,
          cfg.graphQLEndpoint,
          cfg.veritoneAppId
        );
        const linked = auditAssets
          .filter((a) => a.details?.redactedMediaAssetId === versionId)
          .map((a) => a.id);
        yield* call(deleteAuditLogAssets, linked, token, cfg);
      } catch (e) {
        console.warn('Linked audit-log cleanup failed (non-fatal):', e);
      }
    }
  );
}

/**
 * Persist the bumped download count back to the asset's details.
 * The reducer has already incremented `session.versionHistory[i].downloads`
 * optimistically; this saga writes the new value to the server so a future
 * reopen recovers the same count. Mirrors video's INCREMENT_DOWNLOAD_COUNT
 * + updateLastRedactedFileMetrics split.
 *
 * Best-effort: a failed update logs a warning but doesn't roll the local
 * counter back. The next download will retry the write with both increments,
 * so we'll converge on the next successful save.
 */
export function* onIncrementRedactedDocumentDownload() {
  yield* takeEvery(
    INCREMENT_REDACTED_DOCUMENT_DOWNLOAD,
    function* ({ payload: { versionId } }) {
      const session = yield* select(selectSession);
      if (!session) {
        return;
      }
      const entry = session.versionHistory.find(
        (v) => v.versionId === versionId
      );
      if (!entry) {
        return;
      }
      // Build a full details object from what we have in memory so the
      // updateAsset mutation can replace the JSONData field wholesale.
      // GraphQL updateAsset replaces details, not merges — so we have to
      // include every field we care about.
      const details: RedactedDocumentDetails = {
        version: entry.version ?? 1,
        assetSize: entry.assetSize ?? 0,
        numPages: entry.numPages ?? 1,
        startPage: 1,
        endPage: entry.numPages ?? 1,
        metrics: {
          manual: { count: entry.redactionCount },
          // Preserve the per-category metrics — this updateAsset replaces
          // details wholesale, so omitting them would reset them to 0 on the
          // next reopen after a download.
          textSelect: entry.textSelect,
          keywords: entry.keywords,
          PII: entry.pii,
          downloads: entry.downloads,
          // Same wholesale-replace reasoning for the page-level counts.
          pagesRemoved: entry.pagesRemoved,
          pagesFullyRedacted: entry.pagesFullyRedacted,
        },
      };
      const token: string = yield* select(selectToken);
      const cfg = yield* select(selectGraphQLConfig);
      try {
        yield* call(
          serviceUpdateAssetDetails,
          versionId,
          details,
          token,
          cfg.apiRoot,
          cfg.graphQLEndpoint,
          cfg.veritoneAppId
        );
      } catch (e) {
        console.warn(
          'Failed to persist redacted-document download count (non-fatal):',
          e
        );
      }
    }
  );
}

/**
 * Trigger a client-side CSV download. Mirrors the blob-download pattern in
 * CommentsPanel — build a Blob, click a transient `<a download>`, then revoke
 * the object URL so the buffer is freed once the browser takes over.
 */
function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const element = document.createElement('a');
  element.href = url;
  element.download = filename;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  URL.revokeObjectURL(url);
}

/** True if the asset body contains at least one persisted PII match. */
function bodyHasPiiMatches(body: DocumentRedactionAssetV1): boolean {
  return Object.values(body.redactions ?? {}).some(
    (bucket) => (bucket?.PII?.length ?? 0) > 0
  );
}

/**
 * Build the PII-match → PDF-point box map for the report (VE-22300). Loads the
 * engine's vtn-standard result and converts its normalized geometry using a
 * pdfjs page-dimension pass — the same path the editor uses to place PII
 * overlays. Best-effort: any failure yields an empty map (blank PII coords)
 * rather than failing the whole report download.
 */
function* loadPiiBoxes(
  tdoId: string,
  pdfUrl: string,
  token: string,
  cfg: GraphQLConfig
): Generator<unknown, PiiBoxMap, unknown> {
  try {
    const { documentPIIDetectionEngineId } = yield* select(selectConfigEngines);
    if (!documentPIIDetectionEngineId || !pdfUrl) {
      return {};
    }
    const assetUrl = yield* call(
      serviceFindPiiResultAssetUrl,
      tdoId,
      documentPIIDetectionEngineId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    if (!assetUrl) {
      return {};
    }
    const result = yield* call(serviceDownloadPiiResult, assetUrl);
    const pageDimensions = yield* call(fetchPdfPageDimensions, pdfUrl);
    return buildPiiMatchBoxes(result, pageDimensions);
  } catch (e) {
    console.warn('Redaction report: PII geometry load failed (non-fatal):', e);
    return {};
  }
}

/**
 * Load the pages a burned-in output ACTUALLY dropped, for Side-by-Side View.
 *
 * Side-by-Side maps original pages onto redacted ones. Doing that from
 * `session.excludedPages` is wrong the moment the editor diverges from the last
 * burn-in — delete or restore a page and every redacted row after it shifts by
 * one. The redaction-data asset that burn-in consumed is the record of what the
 * output really contains, so read the deleted pages back from there.
 *
 * `takeEvery` keyed on versionId, with the cache checked first: opening
 * Side-by-Side repeatedly costs one fetch per version, not one per open.
 *
 * Failure is deliberately quiet. The consumer falls back to
 * `session.excludedPages`, which is correct whenever the editor has NOT
 * diverged — and Side-by-Side already shows a staleness warning when it has.
 * A second error toast would add noise without telling the user anything they
 * can act on.
 */
export function* handleLoadRedactedVersionPages({
  payload,
}: ReturnType<typeof LOAD_REDACTED_VERSION_PAGES>) {
  const { versionId } = payload;
  const session = yield* select(selectSession);
  // Already cached, or the version isn't in history (deleted while the view was
  // open) — nothing to do either way.
  if (!session || session.redactedExcludedPagesByVersion[versionId]) {
    return;
  }
  const version =
    session.versionHistory.find((v) => v.versionId === versionId) ?? null;
  if (!version) {
    return;
  }
  const token: string = yield* select(selectToken);
  const cfg = yield* select(selectGraphQLConfig);
  try {
    const loaded = yield* call(
      serviceLoadDocumentRedactionDataForRedactedFile,
      session.tdoId,
      version.redactedAt,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId,
      version.version ?? undefined
    );
    if (!loaded) {
      throw new Error('No redaction data found for this redacted file.');
    }
    yield* put(
      LOAD_REDACTED_VERSION_PAGES_SUCCESS({
        versionId,
        excludedPages: excludedPagesFromAssetBody(loaded.body),
      })
    );
  } catch (e) {
    yield* put(
      LOAD_REDACTED_VERSION_PAGES_FAILURE({
        versionId,
        message: e instanceof Error ? e.message : 'Unknown error',
      })
    );
  }
}

export function* onLoadRedactedVersionPages() {
  yield* takeEvery(LOAD_REDACTED_VERSION_PAGES, handleLoadRedactedVersionPages);
}

/**
 * VE-22300: build + download the redaction report for the last redacted file.
 * Loads the `document-redaction-data` asset tied to that file (newest at-or-
 * before its timestamp), formats it into a CSV, and hands it to the browser.
 */
export function* onDownloadRedactionReport() {
  yield* takeEvery(DOWNLOAD_REDACTION_REPORT_REQUEST, function* () {
    const session = yield* select(selectSession);
    if (!session) {
      return;
    }
    const latest =
      session.versionHistory[session.versionHistory.length - 1] ?? null;
    if (!latest) {
      // No redacted file yet — the button shouldn't be reachable, but guard.
      return;
    }
    const token: string = yield* select(selectToken);
    const cfg = yield* select(selectGraphQLConfig);
    try {
      const loaded = yield* call(
        serviceLoadDocumentRedactionDataForRedactedFile,
        session.tdoId,
        latest.redactedAt,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId,
        // Prefer the exact version join over the timestamp heuristic when the
        // output carries a version (VE-26422).
        latest.version ?? undefined
      );
      if (!loaded) {
        throw new Error(
          'No redaction data found for the latest redacted file.'
        );
      }
      // Populate PII geometry only when the report actually has PII matches —
      // avoids the vtn-standard fetch + pdfjs page-dimension pass otherwise.
      const piiBoxes = bodyHasPiiMatches(loaded.body)
        ? yield* call(loadPiiBoxes, session.tdoId, session.pdfUrl, token, cfg)
        : {};
      const csv = formatDocumentRedactionReportCsv(
        loaded.body,
        {
          documentName: session.documentName,
          version: latest.version,
          exportedAt: latest.redactedAt,
        },
        piiBoxes
      );
      const base = session.documentName || 'document';
      const versionSuffix =
        typeof latest.version === 'number' ? ` v${latest.version}` : '';
      downloadCsv(`${base} Redaction Report${versionSuffix}.csv`, csv);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Report download failed';
      yield* put(DOWNLOAD_REDACTION_REPORT_FAILURE({ message }));
      yield* put(
        enqueueSnackbar({
          message: 'Could not download the redaction report. Please try again.',
        })
      );
    }
  });
}
